import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';
import { signIn } from '../auth/authSession';

/**
 * Pre-authentication landing page for the admin portal.
 *
 * Rendered by AuthGuard when no MSAL account is active. Provides a brief
 * "about" panel describing what the portal is for, and a Sign In CTA that
 * triggers the MSAL redirect login. Replaces the previous auto-redirect
 * behaviour where any unauthenticated visit immediately bounced to Entra ID.
 */
export function LandingPage() {
  const { instance } = useMsal();

  const handleSignIn = () => {
    // #24685 — single-flight (a double click starts one redirect), clears a stuck
    // interaction_in_progress marker and retries once, and never rejects unhandled.
    void signIn(instance, loginRequest);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img
              src="/brand/conscierra-icon-fern.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9"
            />
            <div>
              <p className="text-base font-semibold text-gray-900">Conscierra</p>
              <p className="text-xs uppercase tracking-[0.18em] text-gray-500">Admin Portal</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignIn}
            className="inline-flex items-center rounded-md bg-[#21362c] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#17251e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#21362c]"
          >
            Sign in
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-3xl w-full px-4 py-16 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Conscierra Admin Portal
          </h1>
          <p className="mt-5 text-lg leading-8 text-gray-600">
            Internal staff workspace for managing accounts, agreements, cases, billing,
            M365 + procurement, and the operational pulse of the Conscierra platform.
          </p>
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={handleSignIn}
              className="inline-flex items-center rounded-md bg-[#21362c] px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-[#17251e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#21362c]"
            >
              Sign in with Microsoft
            </button>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Authorised Conscierra staff only. Authentication via Microsoft Entra ID.
          </p>
        </div>
      </main>

      <section className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#21362c]">
            About the platform
          </h2>
          <p className="mt-3 text-base text-gray-700 max-w-3xl">
            Conscierra runs on XCentral — a Microsoft-first delivery platform combining
            Managed IT, Digital Solutions &amp; Automation, and Marketing Technology for
            ambitious Australian businesses. The Admin Portal is the operator surface for
            running the engagement: client onboarding, service-tier configuration, case
            management, billing &amp; reconciliation, and M365 / procurement integrations.
          </p>
          <dl className="mt-8 grid gap-6 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Built on</dt>
              <dd className="mt-2 text-sm text-gray-800">
                .NET on Azure App Service, Azure SQL, React + Vite, Microsoft Entra ID.
              </dd>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Compliance</dt>
              <dd className="mt-2 text-sm text-gray-800">
                Certified B Corporation · ISO 9001 + ISO 27001 certified · ISO/IEC 42001 aligned.
              </dd>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-5 py-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Operated by</dt>
              <dd className="mt-2 text-sm text-gray-800">
                B&nbsp;&amp;&nbsp;A Technologies Pty Ltd (trading as Conscierra), Brisbane AU.
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 text-xs text-gray-500">
          © {new Date().getFullYear()} B &amp; A Technologies Pty Ltd. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
